import {
  Badge,
  Card,
  CardHeader,
  CardTable,
  CardTableDataCol,
  CardTableRow,
  Chip,
  CommentIcon,
  IconButton,
  SpinnerButton,
  Theme,
  Typography,
  createStyles,
  makeStyles,
  useRouter,
  useSidebar,
  useTheme,
} from '@forgeglobal/design-framework'
import { calcHumanReadableTimeAgo } from 'util/DateUtils'
import { useGetPaginatedAccountsByOwnersQuery } from 'graphql/bindings/AdminBindings'
import React, { Fragment, useState } from 'react'
import moment from 'moment'

import { AccountActions } from './AccountActions'
import { AccountSidebar } from 'components/accounts/AccountSidebar'
import { Routes } from 'util/Routes'

interface DashboardAccountsTableProps {
  header: string
  owners: number[]
}

const useStyles = makeStyles(({ spacing }: Theme) =>
  createStyles({
    ownersContainer: {
      display: 'flex',
      flexWrap: 'wrap',
    },
  })
)

export const DashboardAccountsTable: React.FC<DashboardAccountsTableProps> = ({ header, owners }) => {
  const defaultTableVars = {
    owners,
    limit: 20,
  }
  const { data, loading, refetch } = useGetPaginatedAccountsByOwnersQuery({
    variables: {
      ...defaultTableVars,
      offset: 0,
    },
    fetchPolicy: 'network-only',
  })

  const { history } = useRouter()
  const theme = useTheme()
  const classes = useStyles()
  const [sidebarDisplayed, setSidebarDisplayed] = useState(false)
  const [expanded, setExpanded] = React.useState<string>('conversations')

  const accounts = data?.getAccountsByOwners.data ?? []

  const sortedAccounts = accounts.sort((a, b) => {
    const aIsAccountOfInterest = a.is_account_of_interest
    const bIsAccountOfInterest = b.is_account_of_interest

    if (aIsAccountOfInterest && !bIsAccountOfInterest) return -1
    if (!aIsAccountOfInterest && bIsAccountOfInterest) return 1

    const aNumActiveOpportunities = a.opportunities.length
    const bNumActiveOpportunities = b.opportunities.length

    if (aNumActiveOpportunities > bNumActiveOpportunities) return -1
    if (aNumActiveOpportunities < bNumActiveOpportunities) return 1

    const aInvestorLevel = a?.account_level ?? 0
    const bInvestorLevel = b?.account_level ?? 0

    if (aInvestorLevel > bInvestorLevel) return -1
    if (aInvestorLevel < bInvestorLevel) return 1

    const aLastContactedAt = a.last_outreach_by_anyone
    const bLastContactedAt = b.last_outreach_by_anyone

    if (!bLastContactedAt || moment(aLastContactedAt).isAfter(moment(bLastContactedAt))) return -1
    if (moment(aLastContactedAt).isBefore(moment(bLastContactedAt))) return 1

    return 0
  })

  const columnDefs = [
    {
      name: 'account',
      label: 'Account',
      minWidth: 150,
    },
    {
      name: 'active_opps',
      label: 'Active Opportunities',
      minWidth: 100,
    },
    {
      name: 'owners',
      label: 'Owners',
      minWidth: 200,
    },
    {
      name: 'last_contact',
      label: 'Last Contacted',
      minWidth: 100,
    },
    {
      name: 'investor_level',
      label: 'Investor Level',
      minWidth: 100,
    },
    {
      name: 'outreach_schedule',
      label: 'Outreach Schedule',
      minWidth: 100,
    },
    {
      name: 'actions',
      label: 'Actions',
      minWidth: 24,
      isLabelHidden: true,
      fixedSize: true,
    },
  ]

  const handleClick = () => {
    setSidebarDisplayed(false)
    setSidebarDisplayed(true)
  }

  return (
    <Fragment>
      <CardTable
        colDefs={columnDefs}
        header={header}
        count={data?.getAccountsByOwners.count}
        onPageChange={(newOffset: number) => {
          refetch({
            ...defaultTableVars,
            offset: newOffset,
          })
        }}
        offset={data?.getAccountsByOwners.offset}>
        {sortedAccounts.map((account: any) => {
          const {
            id,
            name,
            opportunities,
            owners,
            last_outreach_by_anyone,
            account_level,
            outreach_schedule,
            is_account_of_interest,
            interactions,
          } = account
          const columns = {
            account: (
              <CardTableDataCol name="account">
                <Typography variant="subtitle2" onClick={() => history.push(Routes.account(id))}>
                  {name}
                </Typography>
              </CardTableDataCol>
            ),
            active_opps: <Typography variant="subtitle2">{opportunities.length}</Typography>,
            owners: (
              <div className={classes.ownersContainer}>
                {owners.map((owner: any) => (
                  <Chip color="secondary" label={owner.name} key={owner.id} />
                ))}
              </div>
            ),
            last_contact: (
              <Typography variant="subtitle2">
                {last_outreach_by_anyone ? calcHumanReadableTimeAgo(last_outreach_by_anyone) : 'Never'}
              </Typography>
            ),
            investor_level: <Typography variant="subtitle2">{account_level ?? 'n/a'}</Typography>,
            outreach_schedule: (
              <Typography variant="subtitle2">
                {outreach_schedule ? moment.duration(outreach_schedule).format('D [day]') : 'None'}
              </Typography>
            ),
            actions: (
              <Fragment>
                <IconButton aria-label="show-interactions" style={{ marginRight: -12 }}>
                  <Badge variant={interactions.length > 0 ? 'dot' : undefined}>
                    <CommentIcon onClick={handleClick} />
                  </Badge>
                </IconButton>
                <AccountActions accountId={id} />
              </Fragment>
            ),
          }
          return (
            <Fragment key={id}>
              {sidebarDisplayed && (
                <AccountSidebarEnabler accountId={id} expanded={expanded} setExpanded={setExpanded} />
              )}
              <CardTableRow
                id={'1'}
                columns={columns}
                highlightColor={is_account_of_interest ? theme.palette.common.blue : undefined}
              />
            </Fragment>
          )
        })}
        {accounts.length === 0 && !loading && (
          <Card>
            <CardHeader title={'None'} />
          </Card>
        )}
        {loading ? (
          <Card>
            <CardHeader
              title={
                <SpinnerButton
                  variant="text"
                  color="primary"
                  type="submit"
                  fullWidth
                  loading={true}
                  caption="Loading"
                />
              }
            />
          </Card>
        ) : null}
      </CardTable>
    </Fragment>
  )
}

interface AccountSidebarEnablerProps {
  accountId: string
  expanded: string
  setExpanded: (panel: string) => void
}

export const AccountSidebarEnabler: React.FC<AccountSidebarEnablerProps> = ({ accountId, expanded, setExpanded }) => {
  useSidebar(<AccountSidebar accountId={accountId} expanded={expanded} setExpanded={setExpanded} />, [
    expanded,
    setExpanded,
    accountId,
  ])

  return null
}
